import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { Testimonials } from "@/components/sections/testimonials";
import { CTABanner } from "@/components/sections/cta-banner";
import { TeamSection } from "@/components/sections/team";
import {
  Globe, Users, Sparkles, Award, Building2, MapPin, Briefcase, Heart, ArrowRight,
} from "lucide-react";

const stats = [
  { icon: Award, label: "Projects shipped", value: "320+" },
  { icon: Users, label: "Team across 4 cities", value: "60+" },
  { icon: Globe, label: "Clients in", value: "28 countries" },
  { icon: Sparkles, label: "Years in business", value: "8+" },
];

export const Route = createFileRoute("/about/")({
  component: AboutIndex,
  head: () => ({
    meta: [
      { title: "About — CoderLity" },
      { name: "description", content: "CoderLity is a Riyadh & Rangpur-based digital agency building software, SaaS and growth systems worldwide." },
      { property: "og:title", content: "About — CoderLity" },
      { property: "og:description", content: "CoderLity is a Riyadh & Rangpur-based digital agency building software, SaaS and growth systems worldwide." },
      { property: "og:url", content: "/about" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/about" }],
  }),
});

type Item = { slug: string; title: string; desc: string; icon: typeof Users };
const company: Item[] = [
  { slug: "about-us", title: "About Us", desc: "Explore about us", icon: Building2 },
  { slug: "our-team", title: "Our Team", desc: "Explore our team", icon: Users },
  { slug: "branches", title: "Branches", desc: "Explore branches", icon: MapPin },
  { slug: "awards", title: "Awards", desc: "Explore awards", icon: Award },
];
const careers: Item[] = [
  { slug: "open-positions", title: "Open Positions", desc: "Explore open positions", icon: Briefcase },
  { slug: "life-at-coderlity", title: "Life at CoderLity", desc: "Explore life at coderlity", icon: Heart },
];

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.slug}
                to="/about/$child"
                params={{ child: it.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AboutIndex() {
  return (
    <PageShell>
      <PageHero
        eyebrow="About"
        title="CoderLity is a builder's agency"
        sub="We started as a small team of engineers obsessed with shipping. Today we run product, infra and growth for ambitious teams across 28 countries."
      />
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <s.icon className="h-5 w-5 text-primary" />
              <div className="mt-4 text-3xl font-bold text-gradient">{s.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>
      <section className="py-12">
        <div className="mx-auto max-w-5xl px-4 lg:px-8 grid md:grid-cols-2 gap-5">
          {[
            { city: "Riyadh, Saudi Arabia", desc: "HQ · enterprise sales & delivery" },
            { city: "Jeddah, Saudi Arabia", desc: "Client services & support" },
            { city: "CO Bazar, Rangpur, BD", desc: "Engineering & design studio" },
            { city: "Remote · 4 continents", desc: "Specialists, fractionally" },
          ].map((b) => (
            <div key={b.city} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="text-sm font-semibold">{b.city}</div>
              <div className="mt-1 text-sm text-muted-foreground">{b.desc}</div>
            </div>
          ))}
        </div>
      </section>
      <Group title="Company" items={company} />
      <Group title="Careers" items={careers} />
      <TeamSection />
      <Testimonials />
      <CTABanner />
    </PageShell>
  );
}
